import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { forgetRequestsInFlight, getSnapshot, subscribe } from '../../services/requestActivity';

/** The least it shows for, so a page that is quick does not flash it. */
export const MIN_VISIBLE_MS = 400;
/** Nothing on its way to the server for this long: the page's data has arrived. */
export const QUIET_MS = 200;
/** The most it shows for, whatever is still loading. */
export const MAX_VISIBLE_MS = 12000;

const isBusy = (): boolean => getSnapshot() > 0;

interface TopProgressProps {
  /** Where it sits: the shell puts it on the bottom edge of the top bar. */
  className?: string;
}

/**
 * The thin line that runs while a page is arriving.
 *
 * It appears when the address changes (the path: a tab or a filter on the same page is
 * not an arrival) and stays until no request has been in flight for QUIET_MS, for at
 * least MIN_VISIBLE_MS and at most MAX_VISIBLE_MS. It draws nothing the rest of the
 * time, and nothing it does can be clicked or read out.
 */
export function TopProgress({ className = '' }: TopProgressProps) {
  const { pathname } = useLocation();
  const busy = useSyncExternalStore(subscribe, isBusy);

  // One run per arrival. Going from a page to another and straight back is two arrivals,
  // so the run is started again by the change of address, not by the address itself.
  const [run, setRun] = useState({ pathname, done: false });
  if (run.pathname !== pathname) setRun({ pathname, done: false });
  const running = run.pathname === pathname && !run.done;

  const startedAt = useRef(0);

  // The start of a run, and the longest it may last.
  useEffect(() => {
    startedAt.current = Date.now();
    const longest = window.setTimeout(() => {
      // Still waiting after the longest run: stop, and stop waiting for whatever that was,
      // so a request that never reports back cannot hold the line on every later page.
      if (isBusy()) forgetRequestsInFlight();
      setRun((r) => (r.pathname === pathname ? { pathname, done: true } : r));
    }, MAX_VISIBLE_MS);
    return () => window.clearTimeout(longest);
  }, [pathname]);

  // The end of a run: quiet for long enough, and shown for long enough. A request that
  // starts in the meantime cancels the wait, and the wait starts again when it settles.
  useEffect(() => {
    if (!running || busy) return;
    const wait = Math.max(QUIET_MS, startedAt.current + MIN_VISIBLE_MS - Date.now());
    const quiet = window.setTimeout(
      () => setRun((r) => (r.pathname === pathname ? { pathname, done: true } : r)),
      wait,
    );
    return () => window.clearTimeout(quiet);
  }, [pathname, busy, running]);

  if (!running) return null;
  return (
    <div aria-hidden="true" className={`pointer-events-none h-[3px] overflow-hidden ${className}`.trim()}>
      <div className="cp-progress-run h-full bg-primary-600" />
    </div>
  );
}
